import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import { generateAllPageUrls, getCurrentTimeFormatted, getNextUpdateTimeFormatted } from '../utils/qrGenerator';
import { withUrlValidation } from '../components/withUrlValidation';

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'MapoDPP', sans-serif;
`;

const Title = styled.h1`
  color: #333;
  text-align: center;
  margin-bottom: 30px;
`;

const TimeInfo = styled.div`
  background: #f5f5f5;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 30px;
  text-align: center;
`;

const TimeText = styled.p`
  margin: 5px 0;
  font-size: 16px;
  color: #666;
`;

const HighlightText = styled.span`
  color: #4FFFFF;
  font-weight: bold;
`;

const UrlSection = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  color: #333;
  margin-bottom: 15px;
  border-bottom: 2px solid #4FFFFF;
  padding-bottom: 5px;
`;

const UrlCard = styled.div`
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 10px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  gap: 20px;
  align-items: flex-start;
`;

const UrlContent = styled.div`
  flex: 1;
`;

const QrContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: #f9f9f9;
  border-radius: 8px;
  min-width: 120px;
`;

const QrLabel = styled.div`
  font-size: 12px;
  color: #666;
  text-align: center;
  font-weight: bold;
`;

const UrlLabel = styled.div`
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
`;

const UrlText = styled.div`
  background: #f8f8f8;
  padding: 10px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 14px;
  word-break: break-all;
  color: #666;
`;

const CopyButton = styled.button`
  background: #4FFFFF;
  color: #333;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-top: 8px;
  margin-right: 8px;
  transition: background 0.3s;

  &:hover {
    background: #3EEEEE;
  }
`;

const OpenButton = styled.button`
  background: #FF6B6B;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-top: 8px;
  transition: background 0.3s;

  &:hover {
    background: #FF5252;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;



const UrlGeneratorPage = () => {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [currentTime, setCurrentTime] = useState(getCurrentTimeFormatted());
  const [nextUpdateTime, setNextUpdateTime] = useState(getNextUpdateTimeFormatted());

  useEffect(() => {
    generateUrls();
    
    // 1분마다 시간 업데이트
    const timeInterval = setInterval(() => {
      setCurrentTime(getCurrentTimeFormatted());
      setNextUpdateTime(getNextUpdateTimeFormatted());
    }, 60000);

    return () => clearInterval(timeInterval);
  }, []);

  const generateUrls = async () => {
    const generatedUrls = await generateAllPageUrls();
    setUrls(generatedUrls);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('URL이 클립보드에 복사되었습니다!');
    } catch (err) {
      console.error('클립보드 복사 실패:', err);
      alert('클립보드 복사에 실패했습니다.');
    }
  };

  const openUrlInNewTab = (url: string) => {
    window.open(url, '_blank');
  };



  return (
    <Container>
      <TimeInfo>
        <TimeText>현재 시간: <HighlightText>{currentTime}</HighlightText></TimeText>
        <TimeText>다음 QR코드 갱신: <HighlightText>{nextUpdateTime}</HighlightText></TimeText>
      </TimeInfo>

      <UrlSection>
        <SectionTitle>전독시 웹콘텐츠 URL</SectionTitle>
        {Object.entries(urls)
          .filter(([page]) => page === '/')
          .map(([page, url]) => (
            <UrlCard key={page}>
              <UrlContent>
                <UrlLabel>전독시 웹콘텐츠 페이지</UrlLabel>
                <UrlText>{url}</UrlText>
                <ButtonContainer>
                  <CopyButton onClick={() => copyToClipboard(url)}>
                    URL 복사
                  </CopyButton>
                  <OpenButton onClick={() => openUrlInNewTab(url)}>
                    URL로 이동
                  </OpenButton>
                </ButtonContainer>
              </UrlContent>
              <QrContainer>
                <QrLabel>QR 코드</QrLabel>
                <QRCodeSVG value={url} size={100} />
              </QrContainer>
            </UrlCard>
          ))}
        <CopyButton 
          onClick={generateUrls}
          style={{ marginTop: '20px', fontSize: '16px', padding: '12px 24px' }}
        >
          URL 새로고침
        </CopyButton>
      </UrlSection>
    </Container>
  );
};

export default withUrlValidation(UrlGeneratorPage, {
  requireValidation: false, // URL 검증 건너뛰기
  showCountdown: false
});
